import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/**
 * TrackerPage - Contact Monitoring Dashboard
 * Monitor candidates and hiring authorities for career changes
 * Detects job changes, title promotions, company moves
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    }
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `API ${res.status}`);
  }
  return res.json();
};

// Formatters
const fmt = {
  date: (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
  timeAgo: (d) => {
    if (!d) return '—';
    const now = new Date();
    const date = new Date(d);
    const days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  }
};

// Theme hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('kubi_darkMode');
    if (stored !== null) return stored !== 'false';
    return true;
  });

  useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem('kubi_darkMode');
      setIsDark(stored !== 'false');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(handleStorage, 500);
    return () => { window.removeEventListener('storage', handleStorage); clearInterval(interval); };
  }, []);

  return { isDark };
};

// CSS Animations
const styles = `
@keyframes tr-fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes tr-slideIn {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes tr-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
@keyframes tr-glow {
  0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.3); }
  50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.6); }
}
@keyframes tr-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.tr-card-animate { animation: tr-fadeIn 0.4s ease-out forwards; }
.tr-slide-animate { animation: tr-slideIn 0.3s ease-out forwards; }
.tr-hover-lift { transition: transform 0.2s, box-shadow 0.2s; }
.tr-hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
.tr-spin { animation: tr-spin 1s linear infinite; }
.tr-pulse { animation: tr-pulse 2s ease-in-out infinite; }
.tr-glow { animation: tr-glow 2s ease-in-out infinite; }
.tr-change-badge { animation: tr-fadeIn 0.3s ease-out; }
`;

// Icons
const Icons = {
  Users: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  User: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  UserCheck: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>,
  Briefcase: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  Building: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>,
  TrendingUp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  AlertCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  Bell: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  RefreshCw: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Upload: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  Search: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Filter: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  Linkedin: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>,
  Mail: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  Phone: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  ExternalLink: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  Clock: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  CheckCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  XCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  X: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  ArrowRight: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Eye: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  EyeOff: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  Trash2: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>,
  Zap: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Loader: () => <svg className="tr-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  Database: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
};

// Change type configurations
const CHANGE_TYPES = {
  job_change: { label: 'Changed Jobs', color: '#22c55e', icon: Icons.Briefcase, priority: 'high' },
  title_change: { label: 'Title Change', color: '#1E88E5', icon: Icons.TrendingUp, priority: 'medium' },
  company_change: { label: 'New Company', color: '#f59e0b', icon: Icons.Building, priority: 'high' },
  promotion: { label: 'Promotion', color: '#3b82f6', icon: Icons.TrendingUp, priority: 'medium' },
  no_change: { label: 'No Changes', color: '#64748b', icon: Icons.CheckCircle, priority: 'low' }
};

// Contact type configurations - recruiting mode
const RECRUITING_CONTACT_TYPES = {
  candidate: { label: 'Candidate', color: '#1565C0', description: 'Job seekers to track for availability' },
  hiring_authority: { label: 'Hiring Authority', color: '#f59e0b', description: 'Decision makers to track for opportunities' }
};

// Contact type configurations - sales/Spirko AI mode
const SALES_CONTACT_TYPES = {
  prospect: { label: 'Prospect', color: '#1565C0', description: 'Potential clients to track for business opportunities' },
  client_contact: { label: 'Client Contact', color: '#f59e0b', description: 'Existing client contacts to maintain relationships' }
};

// PCR Candidate Status values - from actual PCR dropdown
const PCR_CANDIDATE_STATUSES = [
  { value: 'On Assignment', label: 'On Assignment' },
  { value: 'Candidate', label: 'Candidate' },
  { value: 'Employee', label: 'Employee' },
  { value: 'Internal', label: 'Internal' },
  { value: 'Hiring Authority', label: 'Hiring Authority' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Offer Accepted', label: 'Offer Accepted' },
  { value: 'Placed', label: 'Placed' },
  { value: 'Contractor/Temp', label: 'Contractor/Temp' },
  { value: 'Developed/Qualified', label: 'Developed/Qualified' }
];

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, sub, color = '#1565C0', isDark, highlight = false, pulse = false }) => (
  <div
    className={`tr-card-animate tr-hover-lift ${pulse ? 'tr-glow' : ''}`}
    style={{
      background: highlight ? `linear-gradient(135deg, ${color}, ${color}dd)` : (isDark ? '#1e293b' : '#fff'),
      borderRadius: 16,
      padding: '20px 24px',
      border: highlight ? 'none' : `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
      color: highlight ? '#fff' : 'inherit'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 12, color: highlight ? 'rgba(255,255,255,0.8)' : '#64748b', marginBottom: 8, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 800 }}>{value}</div>
        {sub && <div style={{ fontSize: 13, color: highlight ? 'rgba(255,255,255,0.7)' : '#64748b', marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: highlight ? 'rgba(255,255,255,0.2)' : `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: highlight ? '#fff' : color
      }}>
        <Icon />
      </div>
    </div>
  </div>
);

// Change Alert Card
const ChangeAlertCard = ({ contact, change, isDark, onDismiss, onView }) => {
  const changeConfig = CHANGE_TYPES[change.type] || CHANGE_TYPES.job_change;
  const ChangeIcon = changeConfig.icon;

  return (
    <div
      className="tr-change-badge tr-hover-lift"
      style={{
        background: isDark ? '#1e293b' : '#fff',
        borderRadius: 14,
        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        borderLeft: `4px solid ${changeConfig.color}`,
        padding: 16,
        marginBottom: 12
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: `${changeConfig.color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: changeConfig.color, flexShrink: 0
        }}>
          <ChangeIcon />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              padding: '2px 8px', borderRadius: 6,
              background: `${changeConfig.color}15`, color: changeConfig.color,
              fontSize: 11, fontWeight: 600
            }}>
              {changeConfig.label}
            </span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{fmt.timeAgo(change.detectedAt)}</span>
          </div>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{contact.name}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
            {change.previousValue && (
              <span style={{ textDecoration: 'line-through', marginRight: 8 }}>{change.previousValue}</span>
            )}
            <Icons.ArrowRight style={{ display: 'inline', verticalAlign: 'middle', margin: '0 4px' }} />
            <span style={{ color: changeConfig.color, fontWeight: 500 }}>{change.newValue}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onView(contact)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6,
                background: changeConfig.color, border: 'none',
                color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
              }}
            >
              <Icons.Eye /> View Profile
            </button>
            <button
              onClick={() => onDismiss(contact.id, change.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6,
                background: isDark ? '#334155' : '#f1f5f9',
                border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
                color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer'
              }}
            >
              <Icons.EyeOff /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ contact, isDark, onCheck, onRemove, checking, contactTypes }) => {
  // Support both recruiting and sales type keys
  const allTypes = { ...RECRUITING_CONTACT_TYPES, ...SALES_CONTACT_TYPES };
  const typeConfig = allTypes[contact.type] || contactTypes?.[Object.keys(contactTypes)[0]] || RECRUITING_CONTACT_TYPES.candidate;
  const hasChanges = contact.lastChange && contact.lastChange.type !== 'no_change';

  return (
    <div
      className="tr-card-animate tr-hover-lift"
      style={{
        background: isDark ? '#1e293b' : '#fff',
        borderRadius: 14,
        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        padding: 16,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {hasChanges && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          width: 0, height: 0,
          borderLeft: '30px solid transparent',
          borderTop: `30px solid ${CHANGE_TYPES[contact.lastChange.type]?.color || '#22c55e'}`
        }} />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: `linear-gradient(135deg, ${typeConfig.color}, ${typeConfig.color}aa)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0
        }}>
          {(contact.name || 'U')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{contact.name}</span>
            <span style={{
              padding: '2px 6px', borderRadius: 4,
              background: `${typeConfig.color}15`, color: typeConfig.color,
              fontSize: 10, fontWeight: 600
            }}>
              {typeConfig.label}
            </span>
          </div>
          <div style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 2 }}>
            {contact.currentTitle || 'Title unknown'}
          </div>
          <div style={{ fontSize: 13, color: isDark ? '#64748b' : '#94a3b8', marginBottom: 8 }}>
            {contact.currentCompany || 'Company unknown'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {contact.email && (
              <a href={`mailto:${contact.email}`} style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#64748b', fontSize: 12, textDecoration: 'none' }}>
                <Icons.Mail /> {contact.email}
              </a>
            )}
            {contact.linkedinUrl && (
              <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#0a66c2', fontSize: 12, textDecoration: 'none' }}>
                <Icons.Linkedin /> LinkedIn
              </a>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              <Icons.Clock style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Last checked: {fmt.timeAgo(contact.lastCheckedAt)}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onCheck(contact.id)}
                disabled={checking}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '6px 10px', borderRadius: 6,
                  background: isDark ? '#334155' : '#f1f5f9',
                  border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
                  color: isDark ? '#f8fafc' : '#1f2937',
                  fontSize: 11, fontWeight: 600, cursor: checking ? 'not-allowed' : 'pointer',
                  opacity: checking ? 0.7 : 1
                }}
              >
                <Icons.RefreshCw style={{ animation: checking ? 'tr-spin 1s linear infinite' : 'none' }} />
                Check Now
              </button>
              <button
                onClick={() => onRemove(contact.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: 6,
                  background: 'transparent',
                  border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
                  color: '#ef4444', cursor: 'pointer'
                }}
              >
                <Icons.Trash2 />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// List Folder Icon
const ListIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>;

// Import Modal Component - Search and List (PCR Rollups) only
const ImportModal = ({ isDark, onClose, onImport, loading, contactTypes, isSalesMode }) => {
  const [importType, setImportType] = useState(isSalesMode ? 'prospect' : 'candidate');
  const [source, setSource] = useState('list'); // 'search' or 'list'
  const [rollups, setRollups] = useState([]);
  const [selectedRollup, setSelectedRollup] = useState(null);
  const [loadingRollups, setLoadingRollups] = useState(false);

  // Search form state - proper fields for PCR candidatesV2
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  // Credit info
  const creditsPerContact = 1;
  const monthlyCreditsEstimate = selectedRecords.size * creditsPerContact;

  // Search candidates from PCR candidatesV2
  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim() && !email.trim() && !selectedStatus) return;
    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      if (firstName.trim()) params.append('firstName', firstName.trim());
      if (lastName.trim()) params.append('lastName', lastName.trim());
      if (email.trim()) params.append('email', email.trim());
      if (selectedStatus) params.append('status', selectedStatus);

      const res = await api(`/pcr/candidates/search?${params}`);
      setSearchResults(res.results || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Get record ID consistently (always as string for Set comparisons)
  const getRecordId = (record) => {
    const id = record.CandidateId || record.id || record.CompanyId;
    return id ? String(id) : null;
  };

  // Toggle record selection
  const toggleRecord = (recordId) => {
    const idStr = String(recordId);
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(idStr)) {
      newSelected.delete(idStr);
    } else {
      newSelected.add(idStr);
    }
    setSelectedRecords(newSelected);
  };

  // Select all visible records
  const selectAll = () => {
    if (selectedRecords.size === searchResults.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(searchResults.map(r => getRecordId(r)).filter(Boolean)));
    }
  };

  // Fetch PCR Rollups when source is 'list'
  useEffect(() => {
    if (source === 'list') {
      setLoadingRollups(true);
      api('/pcr/rollups')
        .then(res => {
          setRollups(res.rollups || []);
        })
        .catch(err => {
          console.error('Failed to load rollups:', err);
          setRollups([]);
        })
        .finally(() => setLoadingRollups(false));
    }
  }, [source]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="tr-card-animate kubi-modal-card"
        style={{
          background: isDark ? '#1e293b' : '#fff',
          borderRadius: 20, width: '100%', maxWidth: 560, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column'
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Import Contacts to Track</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <Icons.X />
          </button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Contact Type</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.entries(contactTypes).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setImportType(key)}
                  style={{
                    flex: 1, padding: '14px 16px', borderRadius: 10,
                    background: importType === key ? `${config.color}15` : (isDark ? '#0f172a' : '#f8fafc'),
                    border: `2px solid ${importType === key ? config.color : (isDark ? '#334155' : '#e5e7eb')}`,
                    color: importType === key ? config.color : (isDark ? '#94a3b8' : '#64748b'),
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{config.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.8 }}>{config.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Import Source</label>
            <div className="kubi-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              <button
                onClick={() => { setSource('search'); setSelectedRollup(null); setSearchResults([]); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '14px 12px', borderRadius: 10,
                  background: source === 'search' ? 'rgba(59, 130, 246, 0.15)' : (isDark ? '#0f172a' : '#f8fafc'),
                  border: `2px solid ${source === 'search' ? '#3b82f6' : (isDark ? '#334155' : '#e5e7eb')}`,
                  color: source === 'search' ? '#3b82f6' : (isDark ? '#94a3b8' : '#64748b'),
                  cursor: 'pointer', fontWeight: 600, fontSize: 13
                }}
              >
                <Icons.Search /> Search
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>Query PCR candidates</span>
              </button>
              <button
                onClick={() => { setSource('list'); setSearchResults([]); setSelectedRecords(new Set()); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '14px 12px', borderRadius: 10,
                  background: source === 'list' ? 'rgba(245, 158, 11, 0.15)' : (isDark ? '#0f172a' : '#f8fafc'),
                  border: `2px solid ${source === 'list' ? '#f59e0b' : (isDark ? '#334155' : '#e5e7eb')}`,
                  color: source === 'list' ? '#f59e0b' : (isDark ? '#94a3b8' : '#64748b'),
                  cursor: 'pointer', fontWeight: 600, fontSize: 13
                }}
              >
                <ListIcon /> List
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>From PCR Rollups</span>
              </button>
            </div>
          </div>

          {/* Search Section - PCR candidatesV2 query */}
          {source === 'search' && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Search PCR Candidates</label>

              {/* Search Form Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>First Name</label>
                  <input
                    type="text"
                    placeholder="e.g. John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10,
                      border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                      background: isDark ? '#0f172a' : '#fff',
                      color: isDark ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Last Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Smith"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10,
                      border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                      background: isDark ? '#0f172a' : '#fff',
                      color: isDark ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Email</label>
                  <input
                    type="email"
                    placeholder="e.g. john@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10,
                      border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                      background: isDark ? '#0f172a' : '#fff',
                      color: isDark ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10,
                      border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                      background: isDark ? '#0f172a' : '#fff',
                      color: isDark ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box'
                    }}
                  >
                    <option value="">All Statuses</option>
                    {PCR_CANDIDATE_STATUSES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={handleSearch}
                disabled={searchLoading || (!firstName.trim() && !lastName.trim() && !email.trim() && !selectedStatus)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '12px 16px', borderRadius: 10, border: 'none',
                  background: '#3b82f6', color: '#fff',
                  fontSize: 14, fontWeight: 600,
                  cursor: (searchLoading || (!firstName.trim() && !lastName.trim() && !email.trim() && !selectedStatus)) ? 'not-allowed' : 'pointer',
                  opacity: (searchLoading || (!firstName.trim() && !lastName.trim() && !email.trim() && !selectedStatus)) ? 0.6 : 1
                }}
              >
                {searchLoading ? <Icons.Loader /> : <Icons.Search />} Search Candidates
              </button>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div style={{ borderRadius: 10, border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: isDark ? '#0f172a' : '#f8fafc',
                    borderBottom: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{searchResults.length} results found</div>
                    <button
                      onClick={selectAll}
                      style={{
                        background: 'none', border: 'none', color: '#3b82f6',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer'
                      }}
                    >
                      {selectedRecords.size === searchResults.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {searchResults.map((record, idx) => {
                      const recordId = getRecordId(record);
                      const isSelected = recordId && selectedRecords.has(recordId);
                      return (
                        <button
                          key={recordId || idx}
                          onClick={() => recordId && toggleRecord(recordId)}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 14px', border: 'none',
                            borderBottom: idx < searchResults.length - 1 ? `1px solid ${isDark ? '#334155' : '#e5e7eb'}` : 'none',
                            background: isSelected ? (isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)') : 'transparent',
                            cursor: 'pointer', textAlign: 'left', color: isDark ? '#f8fafc' : '#1f2937'
                          }}
                        >
                          <div style={{
                            width: 20, height: 20, borderRadius: 4,
                            border: `2px solid ${isSelected ? '#3b82f6' : (isDark ? '#475569' : '#cbd5e1')}`,
                            background: isSelected ? '#3b82f6' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {isSelected && <Icons.CheckCircle style={{ color: '#fff', width: 14, height: 14 }} />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{record.Name || record.FirstName + ' ' + record.LastName || 'Unknown'}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {record.Title || record.CurrentTitle || ''} {record.Company || record.CompanyName ? `at ${record.Company || record.CompanyName}` : ''}
                            </div>
                          </div>
                          {record.Status && (
                            <span style={{
                              padding: '2px 8px', borderRadius: 4,
                              background: isDark ? '#334155' : '#e5e7eb',
                              fontSize: 11, color: '#64748b'
                            }}>
                              {record.Status}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {searchResults.length === 0 && (firstName || lastName || email) && !searchLoading && (
                <div style={{ marginTop: 16, padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13, background: isDark ? '#0f172a' : '#f8fafc', borderRadius: 10 }}>
                  No results found. Try adjusting your search criteria.
                </div>
              )}
            </div>
          )}

          {/* PCR Rollups List Selection */}
          {source === 'list' && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Select a PCR Rollup</label>
              {loadingRollups ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, color: '#64748b', gap: 8 }}>
                  <Icons.Loader /> Loading rollups from PCR...
                </div>
              ) : rollups.length === 0 ? (
                <div style={{ padding: 20, background: isDark ? '#0f172a' : '#fef3c7', borderRadius: 10, color: isDark ? '#f59e0b' : '#92400e', fontSize: 13, textAlign: 'center' }}>
                  <Icons.AlertCircle style={{ marginBottom: 8 }} />
                  <div>No rollups found in PCR. Create a rollup in PCRecruiter or use Search instead.</div>
                </div>
              ) : (
                <div style={{ maxHeight: 220, overflowY: 'auto', borderRadius: 10, border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}` }}>
                  {rollups.map((rollup, idx) => (
                    <button
                      key={rollup.id || rollup.code || idx}
                      onClick={() => setSelectedRollup(rollup)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px',
                        background: selectedRollup?.id === rollup.id || selectedRollup?.code === rollup.code ? (isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)') : 'transparent',
                        border: 'none', borderBottom: idx < rollups.length - 1 ? `1px solid ${isDark ? '#334155' : '#e5e7eb'}` : 'none',
                        cursor: 'pointer', textAlign: 'left', color: isDark ? '#f8fafc' : '#1f2937'
                      }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: (selectedRollup?.id === rollup.id || selectedRollup?.code === rollup.code) ? '#f59e0b' : (isDark ? '#334155' : '#e5e7eb'),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: (selectedRollup?.id === rollup.id || selectedRollup?.code === rollup.code) ? '#fff' : '#64748b'
                      }}>
                        <ListIcon />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{rollup.name || rollup.Name || rollup.code || `Rollup ${rollup.id}`}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {rollup.memberCount || rollup.count || 0} members {rollup.description ? `• ${rollup.description}` : ''}
                        </div>
                      </div>
                      {(selectedRollup?.id === rollup.id || selectedRollup?.code === rollup.code) && (
                        <div style={{ color: '#10b981' }}>
                          <Icons.CheckCircle />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{
            padding: 16, background: isDark ? '#0f172a' : '#f8fafc',
            borderRadius: 10, marginBottom: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Icons.Zap style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 600, fontSize: 13 }}>How Tracking Works</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#64748b', fontSize: 12, lineHeight: 1.6 }}>
              <li>We check contacts monthly for career changes</li>
              <li>Get alerts when someone changes jobs or gets promoted</li>
              <li>Perfect for identifying passive {isSalesMode ? 'clients' : 'candidates'} or BD opportunities</li>
            </ul>
          </div>

          {/* Credit Utilization Disclosure */}
          <div style={{
            padding: 14, background: isDark ? 'rgba(245, 158, 11, 0.1)' : '#fef3c7',
            borderRadius: 10, marginBottom: 20, border: `1px solid ${isDark ? 'rgba(245, 158, 11, 0.3)' : '#fcd34d'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Icons.AlertCircle style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? '#fbbf24' : '#92400e' }}>Credit Usage</span>
            </div>
            <div style={{ fontSize: 12, color: isDark ? '#fcd34d' : '#b45309', lineHeight: 1.5 }}>
              {source === 'search' && selectedRecords.size > 0 ? (
                <>
                  <strong>{selectedRecords.size} contacts selected</strong> will use approximately <strong>{monthlyCreditsEstimate} credits/month</strong> for tracking updates.
                </>
              ) : (
                <>
                  Each tracked contact uses <strong>{creditsPerContact} credit/month</strong> for automated career change monitoring. Credits are charged at the beginning of each billing cycle.
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10,
                border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                background: 'transparent',
                color: isDark ? '#f8fafc' : '#1f2937',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (source === 'search') {
                  onImport(importType, source, null, Array.from(selectedRecords));
                } else {
                  // List source - use PCR Rollup code
                  onImport(importType, 'rollup', selectedRollup?.code || selectedRollup?.id);
                }
              }}
              disabled={loading || (source === 'list' && !selectedRollup) || (source === 'search' && selectedRecords.size === 0)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 16px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
                color: '#fff', fontSize: 14, fontWeight: 600,
                cursor: (loading || (source === 'list' && !selectedRollup) || (source === 'search' && selectedRecords.size === 0)) ? 'not-allowed' : 'pointer',
                opacity: (loading || (source === 'list' && !selectedRollup) || (source === 'search' && selectedRecords.size === 0)) ? 0.6 : 1
              }}
            >
              {loading ? <Icons.Loader /> : <Icons.Upload />}
              {loading ? 'Importing...' : source === 'search' && selectedRecords.size > 0 ? `Import ${selectedRecords.size} Contacts` : source === 'list' && selectedRollup ? `Import from ${selectedRollup.name || 'Rollup'}` : 'Import Contacts'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main TrackerPage Component
export default function TrackerPage() {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [changes, setChanges] = useState([]);
  const [filter, setFilter] = useState('all'); // all, candidates/prospects, hiring_authorities/client_contacts, with_changes
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [runningBulkCheck, setRunningBulkCheck] = useState(false);

  // kNine branded modal (replaces native alert/confirm)
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);
  const knAlert = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'alert', message, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
  });
  const knConfirm = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'confirm', message, title: opts.title || 'kNine', variant: opts.variant || 'warning', requireAction: true, ...opts });
  });
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };

  // Detect user mode (sales/Spirko vs recruiting)
  const [userMode, setUserMode] = useState('recruiting'); // 'recruiting' or 'sales'

  useEffect(() => {
    // Load user preferences to determine mode
    const loadUserPrefs = async () => {
      try {
        const res = await api('/ai/settings/user');
        if (res.preferences?.jobFunction === 'sales') {
          setUserMode('sales');
        } else {
          setUserMode('recruiting');
        }
      } catch (err) {
        // Default to recruiting if can't load preferences
        setUserMode('recruiting');
      }
    };
    loadUserPrefs();
  }, []);

  // Get contact types based on mode
  const isSalesMode = userMode === 'sales';
  const CONTACT_TYPES = isSalesMode ? SALES_CONTACT_TYPES : RECRUITING_CONTACT_TYPES;

  // Dynamic labels
  const labels = isSalesMode ? {
    type1: { key: 'prospect', label: 'Prospects' },
    type2: { key: 'client_contact', label: 'Client Contacts' },
    kpi1: 'Prospects',
    kpi2: 'Client Contacts',
    description: 'Monitor prospects and client contacts for business opportunities'
  } : {
    type1: { key: 'candidate', label: 'Candidates' },
    type2: { key: 'hiring_authority', label: 'Hiring Auth.' },
    kpi1: 'Candidates',
    kpi2: 'Hiring Authorities',
    description: 'Monitor candidates and hiring authorities for career changes'
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/tracker/contacts');
      setContacts(res.contacts || []);

      // Extract recent changes
      const recentChanges = [];
      (res.contacts || []).forEach(c => {
        if (c.lastChange && c.lastChange.type !== 'no_change' && !c.lastChange.dismissed) {
          recentChanges.push({ contact: c, change: c.lastChange });
        }
      });
      setChanges(recentChanges.sort((a, b) => new Date(b.change.detectedAt) - new Date(a.change.detectedAt)));
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleImport = async (type, source, listId = null, recordIds = null) => {
    setImporting(true);
    try {
      console.log('[Tracker] Importing:', { type, source, listId, recordIds });
      const result = await api('/tracker/contacts/import', {
        method: 'POST',
        body: JSON.stringify({ type, source, listId, recordIds })
      });
      console.log('[Tracker] Import result:', result);
      setShowImportModal(false);

      // Show success message
      if (result.addedCount > 0) {
        await knAlert(`Successfully imported ${result.addedCount} contact(s)!${result.skipped > 0 ? ` (${result.skipped} duplicates skipped)` : ''}`, { title: 'Import Complete', variant: 'success' });
      } else if (result.skipped > 0) {
        await knAlert(`All ${result.skipped} contact(s) were already in your tracker.`, { title: 'Import', variant: 'info' });
      } else {
        await knAlert('No contacts were imported. Please try again.', { title: 'Import', variant: 'warning' });
      }

      loadData();
    } catch (err) {
      console.error('[Tracker] Import failed:', err);
      await knAlert('Import failed: ' + err.message, { title: 'Import Error', variant: 'error' });
    } finally {
      setImporting(false);
    }
  };

  const handleCheck = async (contactId) => {
    setCheckingId(contactId);
    try {
      await api('/tracker/check', {
        method: 'POST',
        body: JSON.stringify({ contactId })
      });
      loadData();
    } catch (err) {
      await knAlert('Check failed: ' + err.message, { title: 'Error', variant: 'error' });
    } finally {
      setCheckingId(null);
    }
  };

  const handleBulkCheck = async () => {
    const ok = await knConfirm('Run check on all contacts? This may use credits.', { title: 'Bulk Check' });
    if (!ok) return;
    setRunningBulkCheck(true);
    try {
      await api('/tracker/check-all', { method: 'POST' });
      loadData();
    } catch (err) {
      await knAlert('Bulk check failed: ' + err.message, { title: 'Error', variant: 'error' });
    } finally {
      setRunningBulkCheck(false);
    }
  };

  const handleRemove = async (contactId) => {
    const ok = await knConfirm('Remove this contact from tracking?', { title: 'Remove Contact' });
    if (!ok) return;
    try {
      await api(`/tracker/contacts/${contactId}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      await knAlert('Remove failed: ' + err.message, { title: 'Error', variant: 'error' });
    }
  };

  const handleDismissChange = async (contactId, changeId) => {
    try {
      await api(`/tracker/contacts/${contactId}/dismiss`, { method: 'PUT' });
      loadData();
    } catch (err) {
      console.error('Dismiss error:', err);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter contacts
  const filteredContacts = useMemo(() => {
    let filtered = contacts;

    // Handle dynamic filter keys (candidates/prospects, hiring_authorities/client_contacts)
    if (filter === 'candidate' || filter === 'prospect') {
      filtered = filtered.filter(c => c.type === 'candidate' || c.type === 'prospect');
    } else if (filter === 'hiring_authority' || filter === 'client_contact') {
      filtered = filtered.filter(c => c.type === 'hiring_authority' || c.type === 'client_contact');
    } else if (filter === 'with_changes') {
      filtered = filtered.filter(c => c.lastChange && c.lastChange.type !== 'no_change');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.currentTitle || '').toLowerCase().includes(q) ||
        (c.currentCompany || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [contacts, filter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const total = contacts.length;
    // Handle both recruiting (candidate) and sales (prospect) types
    const candidates = contacts.filter(c => c.type === 'candidate' || c.type === 'prospect').length;
    // Handle both recruiting (hiring_authority) and sales (client_contact) types
    const hiringAuthorities = contacts.filter(c => c.type === 'hiring_authority' || c.type === 'client_contact').length;
    const withChanges = contacts.filter(c => c.lastChange && c.lastChange.type !== 'no_change').length;
    return { total, candidates, hiringAuthorities, withChanges };
  }, [contacts]);

  // Inject styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  if (loading && contacts.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 16 }}>
        <Icons.Loader />
        <div style={{ color: '#64748b' }}>Loading tracker...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div className="kubi-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
        <div>
          <h1 className="kubi-page-title" style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800 }}>Contact Tracker</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{labels.description}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleBulkCheck}
            disabled={runningBulkCheck || contacts.length === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px',
              background: isDark ? '#334155' : '#f1f5f9',
              border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
              borderRadius: 10, color: isDark ? '#f8fafc' : '#1f2937',
              fontSize: 14, fontWeight: 600,
              cursor: runningBulkCheck || contacts.length === 0 ? 'not-allowed' : 'pointer',
              opacity: runningBulkCheck || contacts.length === 0 ? 0.7 : 1
            }}
          >
            <Icons.RefreshCw style={{ animation: runningBulkCheck ? 'tr-spin 1s linear infinite' : 'none' }} />
            {runningBulkCheck ? 'Checking...' : 'Check All'}
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px',
              background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}
          >
            <Icons.Plus /> Add Contacts
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KPICard icon={Icons.Users} label="Total Tracking" value={stats.total} sub="contacts monitored" color="#1565C0" isDark={isDark} />
        <KPICard icon={Icons.User} label={labels.kpi1} value={stats.candidates} sub={isSalesMode ? "potential clients" : "job seekers"} color="#3b82f6" isDark={isDark} />
        <KPICard icon={Icons.Briefcase} label={labels.kpi2} value={stats.hiringAuthorities} sub={isSalesMode ? "existing contacts" : "decision makers"} color="#f59e0b" isDark={isDark} />
        <KPICard icon={Icons.Bell} label="Changes Detected" value={stats.withChanges} sub="require attention" color="#22c55e" isDark={isDark} highlight={stats.withChanges > 0} pulse={stats.withChanges > 0} />
      </div>

      {/* Change Alerts */}
      {changes.length > 0 && (
        <div style={{
          background: isDark ? '#1e293b' : '#fff',
          borderRadius: 16, border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          padding: 20, marginBottom: 24
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(34, 197, 94, 0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#22c55e'
            }}>
              <Icons.Bell />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Recent Changes Detected</h3>
            <span style={{
              marginLeft: 'auto', padding: '4px 10px', borderRadius: 20,
              background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e',
              fontSize: 12, fontWeight: 700
            }}>
              {changes.length} new
            </span>
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {changes.slice(0, 5).map((item, idx) => (
              <ChangeAlertCard
                key={item.contact.id + '-' + idx}
                contact={item.contact}
                change={item.change}
                isDark={isDark}
                onDismiss={handleDismissChange}
                onView={(c) => window.open(c.linkedinUrl, '_blank')}
              />
            ))}
          </div>
        </div>
      )}

      {/* Filter & Search */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, background: isDark ? '#1e293b' : '#f1f5f9', padding: 4, borderRadius: 10 }}>
          {[
            { id: 'all', label: 'All' },
            { id: labels.type1.key, label: labels.type1.label },
            { id: labels.type2.key, label: labels.type2.label },
            { id: 'with_changes', label: 'With Changes' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: filter === f.id ? '#1565C0' : 'transparent',
                color: filter === f.id ? '#fff' : (isDark ? '#94a3b8' : '#64748b'),
                fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative' }}>
          <Icons.Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '10px 14px 10px 40px', borderRadius: 10,
              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
              background: isDark ? '#1e293b' : '#fff',
              color: isDark ? '#f8fafc' : '#1f2937',
              fontSize: 14, width: 260
            }}
          />
        </div>
      </div>

      {/* Contacts Grid */}
      {filteredContacts.length === 0 ? (
        <div style={{
          background: isDark ? '#1e293b' : '#fff',
          borderRadius: 16, border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          padding: 60, textAlign: 'center'
        }}>
          <Icons.Users style={{ marginBottom: 12, color: '#64748b', opacity: 0.5 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No contacts being tracked</div>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Import contacts from your ATS to start monitoring for career changes
          </div>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: 10,
              background: 'linear-gradient(135deg, #1565C0, #1E88E5)',
              border: 'none', color: '#fff',
              fontSize: 14, fontWeight: 600, cursor: 'pointer'
            }}
          >
            <Icons.Plus /> Import Contacts
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: 16 }}>
          {filteredContacts.map((contact, idx) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              isDark={isDark}
              onCheck={handleCheck}
              onRemove={handleRemove}
              checking={checkingId === contact.id}
              contactTypes={CONTACT_TYPES}
            />
          ))}
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          isDark={isDark}
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
          loading={importing}
          contactTypes={CONTACT_TYPES}
          isSalesMode={isSalesMode}
        />
      )}

      {/* kNine Branded Modal */}
      {knModal && (
        <>
          <div onClick={() => { if (!knModal.requireAction) handleKnModalClose(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
            <div style={{ background: isDark ? '#1e293b' : '#fff', borderRadius: 16, padding: 20, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: `linear-gradient(135deg, ${knModal.variant === 'success' ? '#10b981' : knModal.variant === 'error' ? '#ef4444' : knModal.variant === 'warning' ? '#f59e0b' : '#1E88E5'}, #1565C0)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700
                }}>K9</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#f8fafc' : '#0f172a' }}>{knModal.title}</h3>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{knModal.message}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {knModal.type === 'confirm' && (
                  <button onClick={() => handleKnModalClose(false)} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#94a3b8' : '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                )}
                <button onClick={() => handleKnModalClose(knModal.type === 'confirm' ? true : true)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #1E88E5, #1565C0)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {knModal.type === 'confirm' ? 'Continue' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
